export const LIFT_KEYS = [
  { value: "", label: "—" },
  { value: "squat", label: "Squat" },
  { value: "bench", label: "Bench" },
  { value: "deadlift", label: "Deadlift" },
  { value: "other", label: "Autre" },
];

export const LIFT_LABELS = {
  squat: "Squat",
  bench: "Bench",
  deadlift: "Deadlift",
  other: "Autre",
};

export const REFERENCE_TYPES = [
  { value: "tm", label: "TM (training max)" },
  { value: "1rm", label: "1RM" },
];

export const INTENSITY_MODES = [
  { value: "percent_tm", label: "% TM" },
  { value: "percent_1rm", label: "% 1RM" },
];

export const DEFAULT_EXERCISE = {
  name: "",
  sets: "",
  reps: "",
  rest: "",
  lift_key: "",
  intensity_pct: "",
  intensity_mode: "percent_tm",
  rpe: "",
};

export function emptyExercise() {
  return { ...DEFAULT_EXERCISE };
}

/** Arrondi à l'incrément de barre (2,5 kg par défaut). */
export function roundToBar(weightKg, increment = 2.5) {
  if (!weightKg || Number.isNaN(weightKg)) return null;
  return Math.round(weightKg / increment) * increment;
}

export function weightFromPercent(maxKg, pct) {
  const max = parseFloat(maxKg);
  const percent = parseFloat(pct);
  if (!max || !percent || Number.isNaN(max) || Number.isNaN(percent)) return null;
  return roundToBar((max * percent) / 100);
}

export function maxesByLift(maxes = []) {
  return Object.fromEntries(
    (maxes || [])
      .filter((m) => m.lift && m.value_kg > 0)
      .map((m) => [m.lift, m])
  );
}

/** Retourne le max applicable selon intensity_mode de l'exercice. */
export function getMaxForExercise(exercise, maxesMap) {
  const liftKey = exercise?.lift_key;
  if (!liftKey || !maxesMap[liftKey]) return null;

  const max = maxesMap[liftKey];
  const mode = exercise.intensity_mode || "percent_tm";

  if (mode === "percent_1rm") {
    if (max.reference_type === "1rm") return max.value_kg;
    return max.value_kg / 0.9;
  }

  if (max.reference_type === "tm") return max.value_kg;
  return max.value_kg * 0.9;
}

export function computeTargetWeight(exercise, maxes = []) {
  const pct = parseFloat(exercise?.intensity_pct);
  if (!pct || Number.isNaN(pct)) return null;

  const map = maxesByLift(maxes);
  const base = getMaxForExercise(exercise, map);
  if (!base) return null;

  return weightFromPercent(base, pct);
}

export function formatIntensityLine(exercise, maxes = []) {
  const pct = exercise?.intensity_pct;
  if (!pct) return null;

  const mode = INTENSITY_MODES.find((m) => m.value === (exercise.intensity_mode || "percent_tm"));
  const target = computeTargetWeight(exercise, maxes);
  const parts = [`${pct}% ${mode?.label?.replace("% ", "") || "TM"}`];

  if (target) parts.push(`${target} kg`);
  if (exercise.rpe) parts.push(`RPE ${exercise.rpe}`);

  return parts.join(" · ");
}

export function normalizeExercise(ex = {}) {
  return {
    name: ex.name || "",
    sets: ex.sets ?? "",
    reps: ex.reps ?? "",
    rest: ex.rest ?? "",
    lift_key: ex.lift_key || "",
    intensity_pct: ex.intensity_pct ?? "",
    intensity_mode: ex.intensity_mode || "percent_tm",
    rpe: ex.rpe ?? "",
  };
}

export function inferLiftFromName(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("squat") || n.includes("flexion")) return "squat";
  if (n.includes("bench") || n.includes("développé") || n.includes("developpe") || n.includes("dc ")) return "bench";
  if (n.includes("deadlift") || n.includes("soulevé") || n.includes("souleve") || n.includes("sdl")) return "deadlift";
  return "";
}

/** Détecte les PR potentiels après une séance loggée. */
export function detectPRsFromSession(exercises = [], liftMaxes = []) {
  const maxByLift = maxesByLift(liftMaxes);
  const prs = [];

  for (const ex of exercises) {
    const liftKey = ex.lift_key || inferLiftFromName(ex.name);
    if (!liftKey || !MAIN_LIFT_KEYS.includes(liftKey)) continue;

    let bestWeight = 0;
    let bestReps = 0;
    for (const set of ex.sets || []) {
      if (!set.completed) continue;
      const w = parseFloat(set.weight_kg);
      const r = parseInt(set.reps, 10);
      if (!w || Number.isNaN(w)) continue;
      if (w > bestWeight) {
        bestWeight = w;
        bestReps = r || 0;
      }
    }

    const currentMax = maxByLift[liftKey]?.value_kg || 0;
    if (bestWeight > 0 && bestWeight > currentMax + 0.24) {
      prs.push({
        lift: liftKey,
        weight_kg: roundToBar(bestWeight) ?? bestWeight,
        reps: bestReps,
        previous_kg: currentMax > 0 ? currentMax : null,
        reference_type: maxByLift[liftKey]?.reference_type || "tm",
      });
    }
  }

  return prs;
}

const MAIN_LIFT_KEYS = ["squat", "bench", "deadlift"];